import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { Button, Icon, Tooltip } from 'antd';
import { FormattedMessage } from 'react-intl';
import messages from '../../../Containers/CandidateDrawer/messages';
import {
  analyzeSms,
  countSmsCharacters,
  getBillableSmsText,
  getSmsCharacterLimit,
  hasPlainTextReplacements,
  replaceWithPlainCharacters,
  MAX_SMS_MESSAGES,
} from '../../../Utils/SmsEncodingUtils';
import styles from './SmsCounter.module.scss';

// Invisible characters (non-breaking or zero-width spaces) would render as an empty chip.
function getCharacterLabel(character) {
  if (character.trim() && !/[\u200B-\u200D\uFEFF]/.test(character)) return character;
  return `U+${character.codePointAt(0).toString(16).toUpperCase().padStart(4, '0')}`;
}

function SmsCounter({ body, consentText, includeConsent, maxMessages, onReplace, readOnly, extra }) {
  const messageBody = body ?? '';
  const billableText = getBillableSmsText(messageBody, consentText, includeConsent);
  const smsInfo = analyzeSms(billableText);
  const characterLimit = getSmsCharacterLimit(smsInfo.isUnicode, maxMessages);
  const isCharacterCountOver = smsInfo.characters > characterLimit;
  const isMessageCountOver = smsInfo.messages > maxMessages;
  const consentCharacters = smsInfo.characters - countSmsCharacters(messageBody, smsInfo.isUnicode);
  const { specialCharacters } = analyzeSms(messageBody);
  const canReplace = !readOnly && !!onReplace && hasPlainTextReplacements(messageBody);
  const messagesSavedByReplacing = canReplace
    ? smsInfo.messages -
      analyzeSms(getBillableSmsText(replaceWithPlainCharacters(messageBody), consentText, includeConsent)).messages
    : 0;

  return (
    <div className={styles.smsCounter}>
      <div className={styles.gauges}>
        <span className={classNames({ [styles.over]: isCharacterCountOver })}>
          <FormattedMessage
            {...messages.smsCharactersUsed}
            values={{ used: smsInfo.characters, limit: characterLimit }}
          />
        </span>
        <span className={styles.separator}>•</span>
        <span className={classNames({ [styles.over]: isMessageCountOver })}>
          <FormattedMessage {...messages.smsMessagesUsed} values={{ used: smsInfo.messages, limit: maxMessages }} />
        </span>
        {consentCharacters > 0 ? (
          <Tooltip
            title={<FormattedMessage {...messages.smsConsentIncludedInCount} values={{ count: consentCharacters }} />}
          >
            <Icon type="info-circle" className={styles.infoIcon} data-testid="sms-consent-info" />
          </Tooltip>
        ) : null}
        {extra}
      </div>
      {smsInfo.isUnicode ? (
        <div className={styles.alert} role="alert">
          <div className={styles.alertTitle}>
            <Icon type="warning" theme="filled" className={styles.warningIcon} />
            <FormattedMessage
              {...(readOnly ? messages.smsReadOnlyUnicodeTemplate : messages.smsSymbolsIncreaseCount)}
            />
          </div>
          {specialCharacters.length ? (
            <div className={styles.characters}>
              <span className={styles.charactersLabel}>
                <FormattedMessage {...messages.smsSpecialCharactersFound} />
              </span>
              {specialCharacters.map(({ character, count, replacement }) => (
                <Tooltip
                  key={character}
                  title={
                    replacement === null ? (
                      <FormattedMessage {...messages.smsNoPlainTextEquivalent} />
                    ) : (
                      <FormattedMessage
                        {...messages.smsReplacedWith}
                        values={{ replacement: replacement === '' ? '∅' : replacement }}
                      />
                    )
                  }
                >
                  <span className={styles.characterChip} data-testid="sms-special-character">
                    <span className={styles.character}>{getCharacterLabel(character)}</span>
                    {count > 1 ? <span className={styles.characterCount}>×{count}</span> : null}
                    {replacement !== null ? (
                      <span className={styles.replacement}>→ {replacement === '' ? '∅' : replacement}</span>
                    ) : null}
                  </span>
                </Tooltip>
              ))}
            </div>
          ) : null}
          {canReplace ? (
            <div className={styles.replaceRow}>
              <Button size="small" onClick={() => onReplace(replaceWithPlainCharacters(messageBody))}>
                <FormattedMessage {...messages.smsReplaceWithPlainText} />
              </Button>
              {messagesSavedByReplacing > 0 ? (
                <span className={styles.savings}>
                  <FormattedMessage {...messages.smsReplaceSavesMessages} values={{ count: messagesSavedByReplacing }} />
                </span>
              ) : null}
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

SmsCounter.propTypes = {
  body: PropTypes.string,
  consentText: PropTypes.string,
  includeConsent: PropTypes.bool,
  maxMessages: PropTypes.number,
  onReplace: PropTypes.func,
  readOnly: PropTypes.bool,
  extra: PropTypes.node,
};

SmsCounter.defaultProps = {
  body: '',
  consentText: '',
  includeConsent: false,
  maxMessages: MAX_SMS_MESSAGES,
  onReplace: undefined,
  readOnly: false,
  extra: null,
};

export default SmsCounter;
